import { ExportResultCode } from '@opentelemetry/core';
export const LOCAL_OTLP_ENDPOINT = '/otlp/v1/traces';
function hrTimeToNanoString(hrTime) {
  return String(BigInt(hrTime[0]) * 1000000000n + BigInt(hrTime[1]));
}
function toOtlpAttributeValue(value) {
  if (typeof value === 'string') {
    return {
      stringValue: value
    };
  }
  if (typeof value === 'boolean') {
    return {
      boolValue: value
    };
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? {
      intValue: String(value)
    } : {
      doubleValue: value
    };
  }
  if (typeof value === 'bigint') {
    return {
      intValue: String(value)
    };
  }
  if (Array.isArray(value)) {
    return {
      arrayValue: {
        values: value.map(v => ({
          value: toOtlpAttributeValue(v)
        }))
      }
    };
  }
  return {
    stringValue: String(value)
  };
}
function toOtlpAttributes(attributes) {
  return Object.entries(attributes).filter(([, v]) => v !== undefined).map(([key, value]) => ({
    key,
    value: toOtlpAttributeValue(value)
  }));
}
export class OtlpExporter {
  constructor(endpoint = LOCAL_OTLP_ENDPOINT, {
    httpClient
  } = {}) {
    this._endpoint = endpoint;
    this._httpClient = httpClient !== null && httpClient !== void 0 ? httpClient : null;
  }
  export(spans, resultCallback) {
    const resourceSpans = this._groupByResource(spans);
    const body = {
      resourceSpans
    };
    const request = this._httpClient ? this._httpClient.post(this._endpoint, {
      data: body
    }) : fetch(this._endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body),
      keepalive: true
    }).then(response => {
      if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
      }
    });
    request.then(() => resultCallback({
      code: ExportResultCode.SUCCESS
    })).catch(err => resultCallback({
      code: ExportResultCode.FAILED,
      error: err instanceof Error ? err : new Error(String(err))
    }));
  }
  shutdown() {
    return Promise.resolve();
  }
  _groupByResource(spans) {
    const resourceMap = new Map();
    for (const span of spans) {
      const resource = span.resource;
      if (!resourceMap.has(resource)) {
        resourceMap.set(resource, []);
      }
      resourceMap.get(resource).push(span);
    }
    return Array.from(resourceMap.entries()).map(([resource, resourceSpans]) => ({
      resource: {
        attributes: toOtlpAttributes(resource.attributes)
      },
      scopeSpans: this._groupByScope(resourceSpans)
    }));
  }
  _groupByScope(spans) {
    const scopeMap = new Map();
    for (const span of spans) {
      var _span$instrumentation;
      const key = `${span.instrumentationScope.name}@${(_span$instrumentation = span.instrumentationScope.version) !== null && _span$instrumentation !== void 0 ? _span$instrumentation : ''}`;
      if (!scopeMap.has(key)) {
        scopeMap.set(key, []);
      }
      scopeMap.get(key).push(span);
    }
    return Array.from(scopeMap.entries()).map(([, scopeSpans]) => {
      var _scope$version;
      const scope = scopeSpans[0].instrumentationScope;
      return {
        scope: {
          name: scope.name,
          version: (_scope$version = scope.version) !== null && _scope$version !== void 0 ? _scope$version : ''
        },
        spans: scopeSpans.map(span => this._toOtlpSpan(span))
      };
    });
  }
  _toOtlpSpan(span) {
    var _span$parentSpanConte;
    const ctx = span.spanContext();
    return {
      traceId: ctx.traceId,
      spanId: ctx.spanId,
      parentSpanId: (_span$parentSpanConte = span.parentSpanContext) === null || _span$parentSpanConte === void 0 ? void 0 : _span$parentSpanConte.spanId,
      name: span.name,
      kind: span.kind + 1,
      startTimeUnixNano: hrTimeToNanoString(span.startTime),
      endTimeUnixNano: hrTimeToNanoString(span.endTime),
      attributes: toOtlpAttributes(span.attributes),
      status: {
        code: span.status.code,
        message: span.status.message
      },
      droppedAttributesCount: span.droppedAttributesCount,
      droppedEventsCount: span.droppedEventsCount,
      droppedLinksCount: span.droppedLinksCount
    };
  }
}